"use client";

import { CallStatus } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import InstallExtension from "@/app/(components)/specifics/extension/InstallExtension/InstallExtension";
import AnalysisDynamics from "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/AnalysisDynamics";
import AnalysisMarket from "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/AnalysisMarket";
import AnalysisOverview from "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/AnalysisOverview/AnalysisOverview";
import CallAnalysisMenu from "@/app/(components)/specifics/tools/CallAnalysis/components/CallAnalysisMenu/CallAnalysisMenu";
import Calls from "@/app/(components)/specifics/tools/CallAnalysis/components/Calls/Calls";
import UploadCalls from "@/app/(components)/specifics/tools/CallAnalysis/components/UploadCalls/UploadCalls";
import OfferRequired from "@/app/(components)/specifics/tools/Offers/components/OfferRequired/OfferRequired";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import PageWrapper from "@/app/(components)/specifics/wrappers/PageWrapper/PageWrapper";
import { useInitialSelection } from "@/app/(components)/specifics/wrappers/SelectionProvider/SelectionProvider";
import { createCall } from "@/app/actions/call/create/action";
import { deleteCall } from "@/app/actions/call/delete/action";
import { renameCall } from "@/app/actions/call/rename/action";
import { updateCallType } from "@/app/actions/call/update-type/action";
import { assignMemberToCall, createProjectMember } from "@/app/actions/project-member/action";
import { useFetchCallAnalysisWorkspace } from "@/app/hooks/fetchs/calls/useFetchCallAnalysisWorkspace";
import { useFetchCalls } from "@/app/hooks/fetchs/calls/useFetchCalls";
import { useFetchProjectCallTypes } from "@/app/hooks/fetchs/calls/useFetchProjectCallTypes";
import { useFetchProjectMembers } from "@/app/hooks/fetchs/project-member/useFetchProjectMembers";
import { useFetchRunningJobs } from "@/app/hooks/fetchs/user/useFetchRunningJobs";
import { useFetchUser } from "@/app/hooks/fetchs/user/useFetchUser";
import { ModalStore } from "@/app/stores/ModalStore";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CallAnalysisBootstrap } from "@/app/types/call/CallAnalysisBootstrap";
import { CallAnalysisTab } from "@/app/types/call/CallAnalysisTab";
import { CallFile, CallFileStatus } from "@/app/types/call/CallFile";
import { CallPeriod, CallPeriodRange } from "@/app/types/call/CallPeriod";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { GetUserCallsResult } from "@/app/types/call/GetUserCallsResult";
import { UserCall } from "@/app/types/call/UserCall";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { ProjectMemberData } from "@/app/types/project/ProjectMember";
import { UCallPeriod } from "@/app/utils/UCallPeriod";
import { UCallScope } from "@/app/utils/UCallScope";
import { UFile } from "@/app/utils/UFile";
import { UFileStorage } from "@/app/utils/UFileStorage";
import { ULogger } from "@/app/utils/ULogger";
import { Globals } from "@/app/vars/Globals";
import { QueryKey, QueryTypeFilter } from "@/app/vars/QueryKeys";
import { UrlParam } from "@/app/vars/Urls";
import {
  filterAndMapNewFiles,
  mapSavedCallsToCallFiles,
  resolveInitialWorkspace,
} from "./CallAnalysis.logic";
import styles from "./CallAnalysis.module.css";

type Props = DefaultProps & {
  bootstrap: CallAnalysisBootstrap | null;
};

export default function CallAnalysis(props: Props) {
  const T = useT();
  const { params, bootstrap } = props;
  const { lang } = params;
  const queryClient = useQueryClient();

  const [savedFiles, setSavedFiles] = useState<CallFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<CallFile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadError, setUploadError] = useState("");

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const _router = useRouter();

  const [memberFilter, setMemberFilter] = useState<number | null | undefined>(() =>
    UCallScope.parseMember(searchParams.get(UrlParam.MEMBER)),
  );

  const [typeFilter, setTypeFilter] = useState<string | null | undefined>(() =>
    UCallScope.parseType(searchParams.get(UrlParam.TYPE)),
  );

  const initialSelection = useInitialSelection();

  const [period, setPeriod] = useState<CallPeriod>(initialSelection.period);

  const [customRange, setCustomRange] = useState<CallPeriodRange>(initialSelection.periodRange);

  const [statusFilter, setStatusFilter] = useState<CallStatus | null | undefined>(() =>
    UCallScope.parseStatus(searchParams.get(UrlParam.STATUS)),
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef<Set<number>>(new Set());
  const user = useAuthUser();
  const canEditContent = useCanEditContent();
  const project = ProjectStore((state) => state.currentProject);
  const offers = OfferStore((state) => state.offers);
  const offersProjectId = OfferStore((state) => state.offersProjectId);
  const currentOfferId = OfferStore((state) => state.currentOfferId);

  const offersKnown = !user || !project || offersProjectId === project.id;
  const showOfferGate = offersKnown && !!user && !!project && !offers.length;
  const projectsFetched = ProjectStore((state) => state.projectsFetched);

  const requireAuth = () => ModalStore.getState().openSignIn(params);

  const { data: runningJobs } = useFetchRunningJobs();

  const analysisFilter = useMemo<CallScopeFilter>(
    () => ({
      memberId: memberFilter,
      type: typeFilter,
      ...UCallPeriod.resolve(period, customRange),
    }),
    [memberFilter, typeFilter, period, customRange],
  );

  const initialWorkspace = resolveInitialWorkspace(
    bootstrap,
    project?.id,
    currentOfferId,
    currentPage,
    statusFilter,
    analysisFilter,
  );

  const { isSuccess: workspaceReady, isLoading: isWorkspaceLoading } =
    useFetchCallAnalysisWorkspace({
      projectId: project?.id,
      offerId: currentOfferId,
      page: currentPage,
      limit: Globals.CALLS_PER_PAGE,
      status: statusFilter,
      filter: analysisFilter,
      enabled: !!user && !!project,
      initialWorkspace,
    });

  const gateEnabled = !!user && !!project && workspaceReady;

  const { data: members = [] } = useFetchProjectMembers({
    projectId: project?.id,
    enabled: gateEnabled,
  });

  const { data: projectUsedTypes = [] } = useFetchProjectCallTypes({
    projectId: project?.id,
    enabled: gateEnabled,
  });

  useFetchUser({
    enabled: !!user,
  });

  const {
    data: callsData,
    isLoading: isCallsLoading,
    refetch: refetchCalls,
  } = useFetchCalls({
    page: currentPage,
    projectId: project?.id,
    offerId: currentOfferId,
    status: statusFilter,
    filter: analysisFilter,
    isCallAnalysisPage: true,
    enabled: gateEnabled,
    runningJobs,
  });

  const callsLoading = isCallsLoading || isWorkspaceLoading;

  const handleMemberFilterChange = (memberId: number | null | undefined) => {
    setMemberFilter(memberId);
    setCurrentPage(1);

    const newParams = new URLSearchParams(window.location.search);
    const serialized = UCallScope.serializeMember(memberId);
    if (serialized === null) {
      newParams.delete(UrlParam.MEMBER);
    } else {
      newParams.set(UrlParam.MEMBER, serialized);
    }

    const queryString = newParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
    window.history.replaceState(null, "", newUrl);
  };

  const handlePeriodChange = (next: CallPeriod, range: CallPeriodRange) => {
    setPeriod(next);
    setCustomRange(range);
    setCurrentPage(1);
    UCallPeriod.store(next, range);
  };

  const handleTypeFilterChange = (type: string | null | undefined) => {
    setTypeFilter(type);
    setCurrentPage(1);

    const newParams = new URLSearchParams(window.location.search);
    const serialized = UCallScope.serializeType(type);
    if (serialized === null) {
      newParams.delete(UrlParam.TYPE);
    } else {
      newParams.set(UrlParam.TYPE, serialized);
    }

    const queryString = newParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
    window.history.replaceState(null, "", newUrl);
  };

  const handleStatusFilterChange = (status: CallStatus | null | undefined) => {
    setStatusFilter(status);
    setCurrentPage(1);

    const newParams = new URLSearchParams(window.location.search);
    if (status === undefined || status === null) {
      newParams.delete(UrlParam.STATUS);
    } else {
      newParams.set(UrlParam.STATUS, status);
    }

    const queryString = newParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
    window.history.replaceState(null, "", newUrl);
  };

  const loadSavedCalls = useCallback(
    async (page: number = 1, showLoader = true) => {
      if (showLoader) setIsLoading(true);
      setCurrentPage(page);
      await refetchCalls();
    },
    [refetchCalls],
  );

  useEffect(() => {
    if (callsData) {
      setSavedFiles(mapSavedCallsToCallFiles(callsData.calls));
      setTotalCount(callsData.totalCount);
      setTotalSize(callsData.totalSize);
      setTotalDuration(callsData.totalDuration);
      setIsLoading(false);
    }
  }, [callsData]);

  useEffect(() => {
    if (callsLoading) {
      setIsLoading(true);
    }
  }, [callsLoading]);

  const openFilePicker = () => {
    if (!user) {
      requireAuth();
      return;
    }

    fileInputRef.current?.click();
  };

  const handleCallCreated = (newCall: UserCall) => {
    if (currentPage !== 1) {
      loadSavedCalls(1);
    }

    queryClient.setQueriesData<GetUserCallsResult>(
      { queryKey: [QueryKey.CALLS], exact: false },
      (oldData) => {
        if (!oldData) return oldData;
        if (oldData.calls.some((c) => c.id === newCall.id)) return oldData;
        return {
          ...oldData,
          calls: [newCall, ...oldData.calls],
          totalCount: oldData.totalCount + 1,
          totalSize: oldData.totalSize + (newCall.fileSize || 0),
          totalDuration: oldData.totalDuration + Math.round(newCall.duration || 0),
        };
      },
    );

    queryClient.invalidateQueries({
      queryKey: [QueryKey.PROJECT_INSIGHTS, project?.id],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.PROJECT_PATTERNS, project?.id],
    });
    queryClient.invalidateQueries({ queryKey: [QueryKey.CALL_ANALYSIS_OVERVIEW] });

    const currentProject = ProjectStore.getState().currentProject;
    if (!currentProject || currentProject.id !== newCall.projectId) {
      ProjectStore.getState().fetchUserProjects();
    }
  };

  const uploadValidFiles = async (validFiles: File[]) => {
    if (!user || validFiles.length === 0) {
      return;
    }

    if (currentPage !== 1) {
      loadSavedCalls(1);
    }

    const callFiles = filterAndMapNewFiles(validFiles, [...uploadingFiles, ...savedFiles]);
    const newUploadingFiles = [...callFiles, ...uploadingFiles];
    setUploadingFiles(newUploadingFiles);

    await Promise.all(
      callFiles.map(async (callFile) => {
        if (!callFile.file) return;
        const file = callFile.file as File;

        if (uploadingRef.current.has(callFile.id)) return;
        uploadingRef.current.add(callFile.id);

        try {
          const duration = await UFile.getMediaDuration(file);

          setUploadingFiles((prev) =>
            prev.map((f) => (f.id === callFile.id ? { ...f, duration } : f)),
          );

          const uniqueFileName = `${crypto.randomUUID()}_${file.name}`;
          const path = UFileStorage.getUserCallPath(user.uuid, uniqueFileName);
          const blobUrl = await UFileStorage.upload(file, path);

          if (!blobUrl) {
            throw new Error("Upload failed");
          }

          const newCall = await createCall({
            name: callFile.label || UFile.getNameWithoutExtension(file.name),
            type: callFile.savedCall?.type,
            fileUrl: blobUrl,
            fileType: UFile.getCallFileType(file),
            fileSize: file.size,
            callDate: callFile.callDate,
            projectId: project?.id,
            offerId: currentOfferId,
            duration,
            memberId: null,
          });

          setUploadingFiles((prev) => prev.filter((f) => f.id !== callFile.id));

          handleCallCreated(newCall);
        } catch (error) {
          ULogger.error("[CallAnalysis] Upload/Create error:", error);
          setUploadingFiles((prev) =>
            prev.map((f) => (f.id === callFile.id ? { ...f, status: CallStatus.FAILED } : f)),
          );
        } finally {
          uploadingRef.current.delete(callFile.id);
        }
      }),
    );
  };

  const handleFilesAdd = async (newFiles: File[]) => {
    setUploadError("");

    if (!user) {
      requireAuth();
      return;
    }

    if (newFiles.some((file) => !UFile.isCallFile(file))) {
      setUploadError(T(TKey.CALL_ANALYSIS_UPLOAD_ERROR_FORMAT) as string);
      return;
    }

    const maxSize = UFile.getMaxCallFileSize();
    const oversized = newFiles.filter((file) => file.size > maxSize);

    await uploadValidFiles(newFiles.filter((file) => file.size <= maxSize));

    if (oversized.length > 0) {
      ModalStore.getState().openModal(
        Components.VideoAudioExtract,
        T(TKey.CALL_ANALYSIS_EXTRACT_AUDIO_TITLE),
        {
          params,
          file: oversized[0],
          onExtracted: (audio: File) => uploadValidFiles([audio]),
        },
        ModalType.SMALL,
      );
    }
  };

  const handleFileRemove = async (id: number) => {
    const callFile = [...uploadingFiles, ...savedFiles].find((f) => f.id === id);
    if (!callFile) return;

    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
    setSavedFiles((prev) => prev.filter((f) => f.id !== id));

    const callId = callFile.savedCall?.id ?? (id > 0 ? id : undefined);
    if (callId) {
      try {
        await deleteCall(callId);

        queryClient.invalidateQueries({
          queryKey: [QueryKey.CALL_ANALYSIS_WORKSPACE, project?.id],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.PROJECT_INSIGHTS, project?.id],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.PROJECT_PATTERNS, project?.id],
        });
        queryClient.invalidateQueries({ queryKey: [QueryKey.CALL_ANALYSIS_OVERVIEW] });

        const newTotalCount = Math.max(0, totalCount - 1);
        setTotalCount(newTotalCount);
        setTotalSize((prev) =>
          Math.max(0, Number(prev) - Number(callFile.savedCall?.fileSize || 0)),
        );

        if (callFile.savedCall?.duration) {
          const removedDuration = callFile.savedCall.duration;
          setTotalDuration((prev) => Math.max(0, prev - Math.round(removedDuration)));
        }

        const remainingOnPage = savedFiles.length - 1;
        if (remainingOnPage === 0 && currentPage > 1) {
          loadSavedCalls(currentPage - 1);
        } else if (newTotalCount > remainingOnPage) {
          loadSavedCalls(currentPage);
        }
      } catch (error) {
        ULogger.error("Failed to delete call:", error);
      }
    }
  };

  const handleFileRename = (id: number, label: string) => {
    const callFile = [...uploadingFiles, ...savedFiles].find((f) => f.id === id);
    setUploadingFiles((prev) => prev.map((f) => (f.id === id ? { ...f, label } : f)));
    setSavedFiles((prev) => prev.map((f) => (f.id === id ? { ...f, label } : f)));

    const callId = callFile?.savedCall?.id ?? (id > 0 ? id : undefined);
    if (callId) {
      renameCall(callId, label)
        .then(() => {
          queryClient.removeQueries({
            queryKey: [QueryKey.CALLS],
            type: QueryTypeFilter.INACTIVE,
          });
          queryClient.invalidateQueries({ queryKey: [QueryKey.CALLS] });
        })
        .catch((e) => ULogger.error("Failed to rename call", e));
    }
  };

  const handleTypeChange = (id: number, type: string | null) => {
    const callFile = [...uploadingFiles, ...savedFiles].find((f) => f.id === id);
    setSavedFiles((prev) =>
      prev.map((f) =>
        f.id === id
          ? {
              ...f,
              savedCall: f.savedCall ? { ...f.savedCall, type } : undefined,
            }
          : f,
      ),
    );

    const callId = callFile?.savedCall?.id ?? (id > 0 ? id : undefined);
    if (callId) {
      updateCallType(callId, type)
        .then(() => {
          queryClient.removeQueries({
            queryKey: [QueryKey.CALLS],
            type: QueryTypeFilter.INACTIVE,
          });
          queryClient.invalidateQueries({ queryKey: [QueryKey.CALLS] });
          queryClient.invalidateQueries({
            queryKey: [QueryKey.PROJECT_CALL_TYPES],
          });
          queryClient.invalidateQueries({ queryKey: [QueryKey.CALL_ANALYSIS_OVERVIEW] });
        })
        .catch((e) => ULogger.error("Failed to update call type", e));
    }
  };

  const handleStatusUpdate = (id: number, status: CallFileStatus) => {
    setSavedFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
  };

  const assignMember = (id: number, member: { id: number; name: string } | null) => {
    const callFile = [...uploadingFiles, ...savedFiles].find((f) => f.id === id);
    const callId = callFile?.savedCall?.id ?? (id > 0 ? id : undefined);

    if (!callId) {
      setSavedFiles((prev) =>
        prev.map((f) =>
          f.id === id
            ? {
                ...f,
                savedCall: f.savedCall ? { ...f.savedCall, member } : undefined,
              }
            : f,
        ),
      );
      return;
    }

    const newMember = member ? { id: member.id, name: member.name } : null;
    const newMemberId = newMember?.id ?? null;

    queryClient
      .getQueryCache()
      .findAll({ queryKey: [QueryKey.CALLS], type: QueryTypeFilter.ACTIVE })
      .forEach((query) => {
        const data = query.state.data as GetUserCallsResult | undefined;
        if (!data?.calls.some((c) => c.id === callId)) {
          return;
        }

        const filterMemberId = query.queryKey[4] as number | null | "all";
        const matchesFilter =
          filterMemberId === "all" ||
          (filterMemberId === null && newMemberId === null) ||
          filterMemberId === newMemberId;

        queryClient.setQueryData<GetUserCallsResult>(
          query.queryKey,
          matchesFilter
            ? {
                ...data,
                calls: data.calls.map((c) => (c.id === callId ? { ...c, member: newMember } : c)),
              }
            : {
                ...data,
                calls: data.calls.filter((c) => c.id !== callId),
                totalCount: Math.max(0, data.totalCount - 1),
              },
        );
      });

    queryClient.removeQueries({
      queryKey: [QueryKey.CALLS],
      type: QueryTypeFilter.INACTIVE,
    });
    queryClient.invalidateQueries({ queryKey: [QueryKey.CALL_ANALYSIS_OVERVIEW] });

    assignMemberToCall(callId, newMemberId).catch((e) =>
      ULogger.error("Failed to assign member to call", e),
    );
  };

  const handleMemberChange = (id: number, memberId: number | null) => {
    const member = memberId !== null ? members.find((m) => m.id === memberId) : undefined;
    assignMember(id, member ? { id: member.id, name: member.name } : null);
  };

  const handleAddMember = (callId: number) => {
    if (!project) {
      return;
    }
    ModalStore.getState().openProjectMemberForm(params, project.id, {
      onSaved: (member: ProjectMemberData) =>
        assignMember(callId, { id: member.id, name: member.name }),
    });
  };

  const handleSelfAssign = async (callId: number) => {
    if (!user || !project) return;

    let userMember = members.find((m) => m.userId === user.id);

    if (!userMember) {
      const result = await createProjectMember(project.id, {
        email: user.email,
      });
      if (!result.ok || !result.member) return;
      userMember = result.member;
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROJECT_MEMBERS, project.id],
      });
    }

    assignMember(callId, { id: userMember.id, name: userMember.name });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFilesAdd(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (!user) {
      setSavedFiles([]);
      setUploadingFiles([]);
      setTotalCount(0);
      setCurrentPage(1);
      setIsLoading(false);
      return;
    }
  }, [user]);

  useEffect(() => {
    if (user && projectsFetched && !project) {
      setIsLoading(false);
    }
  }, [user, projectsFetched, project]);

  const allFiles = memberFilter == null ? [...uploadingFiles, ...savedFiles] : savedFiles;
  const hasAnalyzedFiles = allFiles.some((f) => f.status === CallStatus.ANALYZED);

  return (
    <PageWrapper
      title={T(TKey.CALL_ANALYSIS_TITLE)}
      subtitle={T(TKey.CALL_ANALYSIS_SUBTITLE)}
      rightElement={
        <div className={styles.headerActions}>
          <InstallExtension lang={lang} />
          {user && project && hasAnalyzedFiles && (
            <CallAnalysisMenu params={params} filter={analysisFilter} />
          )}
        </div>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={UFile.getCallAccept()}
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      {!offersKnown ? null : showOfferGate ? (
        <div className={styles.offerGate}>
          <OfferRequired params={params} />
        </div>
      ) : (
        <div className={styles.mainBody}>
          <div className={styles.content}>
            {canEditContent && (
              <div className={styles.uploadSection}>
                <UploadCalls
                  params={params}
                  files={allFiles}
                  onFilesAdd={handleFilesAdd}
                  onFileRemove={handleFileRemove}
                  onBrowse={openFilePicker}
                  onCallCreated={handleCallCreated}
                  isAuthenticated={!!user}
                  onRequireAuth={requireAuth}
                  uploadError={uploadError}
                  setUploadError={setUploadError}
                />
              </div>
            )}

            <div className={styles.librarySection}>
              <Calls
                params={params}
                files={allFiles}
                totalCount={totalCount}
                totalSize={totalSize}
                totalDuration={totalDuration}
                currentPage={currentPage}
                onPageChange={loadSavedCalls}
                isLoading={isLoading}
                onFileRemove={handleFileRemove}
                onFileRename={handleFileRename}
                onTypeChange={handleTypeChange}
                onStatusUpdate={handleStatusUpdate}
                onUploadClick={openFilePicker}
                members={members}
                onMemberChange={handleMemberChange}
                onAddMember={handleAddMember}
                onSelfAssign={handleSelfAssign}
                memberFilter={memberFilter}
                onMemberFilterChange={handleMemberFilterChange}
                typeFilter={typeFilter}
                onTypeFilterChange={handleTypeFilterChange}
                statusFilter={statusFilter}
                onStatusFilterChange={handleStatusFilterChange}
                period={period}
                customRange={customRange}
                onPeriodChange={handlePeriodChange}
                projectUsedTypes={projectUsedTypes}
              />
            </div>
          </div>

          {(!user || hasAnalyzedFiles) && (
            <div className={styles.analysisContainer}>
              <TabbedContent
                urlParamName={UrlParam.TAB}
                initialItemId={user ? CallAnalysisTab.OVERVIEW : CallAnalysisTab.MARKET}
                disableScrollOnTabChange
                items={[
                  {
                    id: CallAnalysisTab.OVERVIEW,
                    title: T(TKey.CALL_ANALYSIS_TAB_OVERVIEW),
                    content: (
                      <AnalysisOverview
                        params={params}
                        filter={analysisFilter}
                        enabled={!!user && !!project}
                      />
                    ),
                  },
                  {
                    id: CallAnalysisTab.MARKET,
                    title: T(TKey.CALL_ANALYSIS_TAB_MARKET),
                    content: (
                      <AnalysisMarket
                        params={params}
                        filter={analysisFilter}
                        callsLoading={callsLoading}
                      />
                    ),
                  },
                  {
                    id: CallAnalysisTab.PATTERNS,
                    title: T(TKey.CALL_ANALYSIS_TAB_PATTERNS),
                    content: (
                      <AnalysisDynamics
                        params={params}
                        filter={analysisFilter}
                        callsLoading={callsLoading}
                      />
                    ),
                  },
                ]}
              />
            </div>
          )}
        </div>
      )}
    </PageWrapper>
  );
}
